import React, { useCallback, useEffect, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { ReactComponent as PartsNotFoundImage } from 'assets/images/parts-not-found.svg';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';
import { useTranslation } from 'react-i18next';
import useHttp from 'utils/hooks/useHttp';
import { useLocation, useSearchParams } from 'react-router-dom';
import UniversalPartsNavbar from 'components/navbar/universal-parts-navbar/UniversalPartsNavbar';
import { IBatteryCatalogFilter, IBodyResponseBattery } from 'interfaces/battery';
import ProductsViewMode from 'components/ProductsViewMode/ProductsViewMode';
import { DEFAULT_VIEW, GRID_VIEW } from 'components/ProductsViewMode/ProductsViewMode.constants';
import ProductsViewModeChanger from 'components/ProductsViewModeChanger/ProductsViewModeChanger';
import { CATALOG_BATTERY } from '../../../../components/ProductsViewModeChanger/products-view-mode-changer-utils';
import { useMobileDetect } from '../../../../utils/hooks/use-mobile-detect';
import List from 'components/List/List';
import { ICategory } from 'views/Sidebar/Sidebar.model';
import './CatalogsBatteriesPage.scss';
import { sortByOptions } from 'components/SortBy/SortBy.map';
import SortBy, { SortByParams } from 'components/SortBy/SortBy';
import CatalogBatteryFilter from 'applications/retailer/views/CatalogBatteries/CatalogBatteryFilter/CatalogBatteryFilter';

const CatalogsBatteriesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const http = useHttp();
  const { search } = useLocation();

  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [refData, setRefData] = useState<IBodyResponseBattery>();
  const [filterData, setFilterData] = useState<IBatteryCatalogFilter>();
  const [openFilter, setOpenFilter] = useState(false);
  const [page, setPage] = useState(Number(searchParams.get('page')) ? Number(searchParams.get('page')) : 1);

  const [batteryCategories, setBatteryCategories] = useState<ICategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<{ id: number }>({ id: searchParams.get('category_id') ? Number(searchParams.get('category_id')) : batteryCategories[0]?.id });

  const user = JSON.parse(localStorage.getItem('userData')!);
  const [viewMode, setViewMode] = useState(user?.user_settings.products_view_mode || DEFAULT_VIEW);
  const [cancelToken, setCancelToken] = useState<CancelTokenSource | undefined>(undefined);

  const isMobile = useMobileDetect();

  useEffect(() => {
    if (isMobile) {
      setViewMode(GRID_VIEW);
    }
  });

  function getBatteryCategories() {
    setLoadingCategories(true);
    http.catalogs.getCarBatteriesAllCategories({}).then((res) => {
      setBatteryCategories(res);
    }).finally(() => setLoadingCategories(false));
  }

  const getBatteriesData = useCallback((data) => {
    setLoading(true);


    if (cancelToken) {
      cancelToken.cancel();
    }

    const source = axios.CancelToken.source();

    setCancelToken(source);

    const sortBySelectedOption = sortByOptions.filter((item) => String(item.value) == String(searchParams.get('sort')))[0] ?? [];

    const sortParams: SortByParams = {
      sort_by_column: String(searchParams.get('sort') ?? ''),
      sort_by_asc: String(searchParams.get('asc') ?? sortBySelectedOption?.default_sort_asc ?? ''),
    };

    const params = {
      ...data,
      ...sortParams,
      page,
    };

    const validParams = validateParams(params);

    http.batteriesHttp
      .getBatteries(validParams, source.token)
      .then((res) => {
        setRefData(res.data);
        setLoading(false);
        setTimeout(() => {
          if (params.page) {
            const firstChild = document.querySelector('.mecadepot-catalog-item');

            if (firstChild) {
              firstChild.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
          }
        }, 100);
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          setLoading(false);
        }
      });
  }, [cancelToken, searchParams.get('sort'), searchParams.get('asc')]);

  function getBatteriesFilter() {
    const filterParams = {
      page,
    };

    const validFilterParams = validateParams(filterParams);

    http.batteriesHttp.getBatteryFilters(validFilterParams).then((res) => {
      setFilterData(res.data);
    });
  }

  const validateParams = (data) => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      acc[key] = value;

      return acc;
    }, {});
  };

  const handlePageClick = (pageN: number) => {
    setPage(pageN);
  };

  const onCategoryClick = (category) => {
    setActiveCategory({ id: category.id });
    searchParams.set('category_id', String([category?.id]));
    searchParams.delete('page');
    setSearchParams(searchParams);
    setPage(1);
  };

  useEffect(() => {
    if (batteryCategories.length) {
      const firstValidCategory = batteryCategories.find((category) => category.number_of_products !== 0);

      setActiveCategory({ id: Number(searchParams.get('category_id')) || Number(firstValidCategory?.id) });

      const params = {
        category_ids: searchParams.get('category_id') ? searchParams.getAll('category_id') : [String(firstValidCategory?.id)],
        brand_ids: searchParams.getAll('brand_ids'),
        ah: searchParams.getAll('ah'),
        amp: searchParams.getAll('amp'),
      };

      const validParams = validateParams(params);

      getBatteriesData(validParams);
      getBatteriesFilter();
    }
  }, [
    i18n.language,
    page,
    search,
    batteryCategories.length
  ]);

  useEffect(() => {
    getBatteryCategories();
  }, [i18n.language]);

  useEffect(() => {
    if (searchParams.get('category_id')) {
      setActiveCategory({ id: Number(searchParams.get('category_id')) });
    }
  }, [searchParams.get('category_id')]);

  function filterOptionsElement() {
    return (
      <div className="catalog-parts-filter">
        <button onClick={() => setOpenFilter(true)} className={`catalog-parts-button ${openFilter ? 'active' : ''}`}>
          {t('_filter_results_btn')}
        </button>
      </div>
    );
  }

  return (
    <div className="batteries-catalog-wrapper">
      <UniversalPartsNavbar option={filterOptionsElement()} title="_find_neccessary_parts" />
      <div className="separator"></div>

      <div className="filterOptions">
        {refData?.data?.length && refData?.data?.length > 0 ? <SortBy /> : null}
        <div className="results">
          {refData?.total_count} {t('_results')}
        </div>
        <ProductsViewMode viewMode={viewMode} setViewMode={setViewMode} />
      </div>

      <div className="catalog-batteries-items-container">

        <div className="catalog-batteries-sidebar">
          {batteryCategories && batteryCategories.length ? (
            <List
              content={batteryCategories?.map((item) => {
                return { ...item, id: item.id, name: item.name };
              })}
              images={batteryCategories?.map((item) => item.image)}
              itemClassName="parts-list"
              itemId="id"
              onItemClick={onCategoryClick}
              activeCategory={activeCategory}
            />
          ) : null}
        </div>
        <div className="wrapperItems">
          {loadingCategories || loading ? (
            <Spinner class="car-parts-spinner" />
          ) : refData?.data?.length ? (
            <ProductsViewModeChanger catalog={CATALOG_BATTERY} viewMode={viewMode} data={refData?.data} />
          ) : (
            <div className="no-parts-data">
              <div className="notFoundSvg">
                <PartsNotFoundImage />
              </div>
              <h1>{t('_batteries_not_found')}</h1>
            </div>
          )}
          {(refData && refData?.total_pages && refData?.page && refData?.total_pages > 1) && !loading ? (
            <Pagination pageCount={refData.total_pages} handlePageClick={handlePageClick} forcePage={page} />
          ) : (
            ''
          )}
        </div>
      </div>

      <CatalogBatteryFilter getData={getBatteriesData} filterData={filterData} openFilter={openFilter} setOpenFilter={setOpenFilter} />
    </div>
  );
};

export default CatalogsBatteriesPage;
